import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listMySchools, getMyStudentInfo } from "../happyschool_static/happyschoolApi";
import { fetchCatalog } from "./quiz3Utils";

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// Bài kiểm tra có chọn kiểu hiển thị (displayMode) thì truyền sang trang làm bài
// qua ?mode= — ghi đè kiểu của riêng đề đó (xem Quiz3QuizPlayer).
const playUrl = (quizId, exam) =>
  `/quiz3/play/${quizId}` + (exam && (exam.displayMode === "animated" || exam.displayMode === "both") ? `?mode=${exam.displayMode}` : "");

// GEN-01b — Học sinh vừa vào Quiz3 thì tự kiểm tra lớp mình (bên HappySchool)
// có bài kiểm tra nào đang MỞ mà CHƯA làm không, có thì hiện ngay để bấm vào
// làm luôn, không cần đợi giáo viên nhắc. Dùng thẳng API HappySchool đã có
// sẵn (getMyStudentInfo) — không cần thêm endpoint mới. Im lặng (render null)
// với tài khoản không phải học sinh/không có bài mở/lỗi mạng — đây là tính
// năng phụ, không được làm hỏng trang chủ Quiz3 (vẫn công khai cho mọi người).
const Quiz3OpenExamBanner = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [quizTitleMap, setQuizTitleMap] = useState({});
  const [subjectIdByName, setSubjectIdByName] = useState({});

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Chỉ cần tên đề/môn -> catalog nhẹ, dùng chung với trang chọn đề/làm bài.
        const [schools, allData] = await Promise.all([listMySchools(), fetchCatalog()]);
        if (cancelled) return;

        const titleMap = {};
        Object.entries(allData.quizzes || {}).forEach(([id, q]) => { titleMap[id] = q.title; });
        const subjMap = {};
        Object.entries(allData.subjects || {}).forEach(([id, s]) => { subjMap[norm(s.name)] = id; });
        setQuizTitleMap(titleMap);
        setSubjectIdByName(subjMap);

        const studentSchools = (schools || []).filter((s) => s.role === "student");
        const results = await Promise.all(
          studentSchools.map((s) => getMyStudentInfo(s.id).catch(() => null))
        );
        if (cancelled) return;

        const openUndone = [];
        results.forEach((info, idx) => {
          if (!info || !info.exams) return;
          info.exams.forEach((exam) => {
            if (exam.status === "closed") return;
            if (exam.endAt && Date.now() > exam.endAt) return; // hết hạn làm bài
            if (exam.score !== "" && exam.score !== undefined) return; // đã làm rồi
            if (!exam.quizId && !(exam.quizIds && exam.quizIds.length) && !exam.subject) return;
            openUndone.push({ ...exam, schoolId: studentSchools[idx].id });
          });
        });
        setPending(openUndone);
      } catch (e) {
        // Tính năng phụ — im lặng khi lỗi, không chặn trang chủ Quiz3.
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  if (pending.length === 0) return null;

  const fmt = (ms) => new Date(ms).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
      {pending.map((exam) => {
        const notYet = !!(exam.startAt && Date.now() < exam.startAt);
        return (
        <div key={`${exam.schoolId}-${exam.id}`} style={cardStyle}>
          <div>
            <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#92400e" }}>
              🔔 Bài kiểm tra đang mở: {exam.title || exam.subject}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: "#78350f" }}>
              {exam.subject ? `Môn: ${exam.subject}` : ""}{exam.date ? ` · Ngày: ${exam.date}` : ""}
              {exam.startAt ? ` · Bắt đầu: ${fmt(exam.startAt)}` : ""}{exam.endAt ? ` · Hạn chót: ${fmt(exam.endAt)}` : ""}
            </p>
          </div>
          {notYet ? (
            <span style={doneChipStyle}>⏳ Mở lúc {fmt(exam.startAt)}</span>
          ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {exam.quizId && (
              <button style={btnStyle} onClick={() => navigate(playUrl(exam.quizId, exam))}>
                ▶ Vào làm: {quizTitleMap[exam.quizId] || "Bắt đầu"}
              </button>
            )}
            {!exam.quizId && Array.isArray(exam.allQuizIds) && exam.allQuizIds.map((qid) => {
              const done = Array.isArray(exam.doneQuizIds) && exam.doneQuizIds.includes(qid);
              return done ? (
                <span key={qid} style={doneChipStyle}>✅ {quizTitleMap[qid] || "Đã làm"}</span>
              ) : (
                <button key={qid} style={btnStyle} onClick={() => navigate(playUrl(qid, exam))}>
                  ▶ {quizTitleMap[qid] || "Vào làm"}
                </button>
              );
            })}
            {!exam.quizId && !exam.allQuizIds && Array.isArray(exam.quizIds) && exam.quizIds.map((qid) => (
              <button key={qid} style={btnStyle} onClick={() => navigate(playUrl(qid, exam))}>
                ▶ {quizTitleMap[qid] || "Vào làm"}
              </button>
            ))}
            {!exam.quizId && !(exam.quizIds && exam.quizIds.length) && exam.subject && (
              <button
                style={btnStyle}
                onClick={() => navigate("/quiz3/take", { state: { subjectId: subjectIdByName[norm(exam.subject)] || null } })}
              >
                Chọn đề trong môn {exam.subject}
              </button>
            )}
          </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

const cardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  padding: "14px 16px",
  border: "1px solid #fde68a",
  background: "#fffbeb",
  borderRadius: 12,
};

const doneChipStyle = {
  padding: "8px 14px",
  background: "#ecfdf5",
  color: "#059669",
  border: "1px solid #a7f3d0",
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 13,
  whiteSpace: "nowrap",
};

const btnStyle = {
  padding: "8px 14px",
  background: "#d97706",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
  whiteSpace: "nowrap",
};

export default Quiz3OpenExamBanner;
